// MCActivityPage — Tela Atividade (/mc/activity) · Correção UX v2 §5.5
// (wireframe docs/RAIOS-UX-SIDEBAR-SPEC.html, aba "06 Atividade").
//
// PRINCÍPIO ZERO: réplica LITERAL do HTML bruto da seção 06 —
//   <div class="dfilters"> busca (.search) + select Tipo + select Status </div>
//   <div class="dblock"><div class="dblock-h">icon Atividade <live></div>
//     <div class="afeed big"> .arow.click* </div></div>
// Classes portadas: base W0 (wireframe.css: .wf-feed/.wf-live/...) +
// específicas §06 (wf-activity.css: .wf-dfilters/.wf-search/.wf-dblock/...).
//
// Dados: feed real-time (mais recente → mais antiga) via mc-store — realtime
// nas tabelas SUPORTADAS (skill/ephemeral/handler/companion) + polling
// incremental protegido cobrindo agent_executions (particionada, sem realtime;
// W1.4): 30s, watermark obrigatório, pausa em background e backoff em falha.
// O pill de status no header do bloco mostra "live" quando o realtime está OK
// (polling rodando não é erro).
//
// Filtros Tipo (pipeline/skill/agent/companion/tool) e Status (ok/em execução/
// falha) são repassados ao servidor (typeToKindParam/statusToBackendParam) E
// re-aplicados client-side — inserts de realtime/polling chegam sem filtro e
// não podem vazar. Clique numa linha abre ActivityDetailModal (métricas ·
// quando · identificação · input · output · erro — SEM "Execuções filhas").

import '@/styles/wf-activity.css';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useMCStore } from '@/stores/mc-store';
import { ActivityFeedRow } from '@/components/activity/ActivityFeedRow';
import { ActivityDetailModal } from '@/components/activity/ActivityDetailModal';
import {
  type ActivityStatusFilter,
  type ActivityTypeFilter,
  STATUS_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS,
  filterActivityFeed,
  statusToBackendParam,
  typeToKindParam,
} from '@/components/activity/activity-feed-filters';
import { PageHeader } from '@/components/layout/PageHeader';
import { WfEmpty } from '@/components/wf';
import { Skeleton } from '@/components/ui/Skeleton';
import { Activity, Loader2, Search } from 'lucide-react';

/* ── Indicador de conexão (pill do .dblock-h — wireframe <span class="live">) ── */

function RealtimeIndicator() {
  const state = useMCStore((s) => s.activityChannelState);
  // joined = anatomia .live literal do wireframe. Demais estados usam a pill
  // de badge da base W0 (.wf-badge) — polling ativo NÃO é erro (W1.4): só
  // falha de assinatura realtime SUPORTADA vira "Erro de conexão".
  if (state === 'joined') return <span className="wf-live">live</span>;
  const config = {
    idle: { label: 'Conectando…', cls: 'wf-badge-neutral' },
    connecting: { label: 'Conectando…', cls: 'wf-badge-amber' },
    disconnected: { label: 'Reconectando…', cls: 'wf-badge-amber' },
    error: { label: 'Erro de conexão', cls: 'wf-badge-danger' },
  }[state];
  return <span className={`wf-badge ${config.cls}`}>{config.label}</span>;
}

/* ── Page ────────────────────────────────────────────────────────────────────── */

export function MCActivityPage() {
  const { tenant } = useAuth();
  const {
    activity,
    activityLoading,
    activityHasMore,
    activityFilters,
    fetchActivity,
    loadMoreActivity,
    setActivityFilters,
    subscribeActivityRealtime,
    unsubscribeActivityRealtime,
    openActivityDetail,
  } = useMCStore();

  // Mount: hidrata + assina realtime/polling (idempotente quando
  // subscribeRealtime já foi chamado). Se o store ficou com kind/status de uma
  // visita anterior, re-sincroniza com os defaults locais ('todos') —
  // setActivityFilters já refaz o fetch.
  useEffect(() => {
    if (activityFilters.kind !== 'all' || activityFilters.status !== 'all') {
      setActivityFilters({ kind: 'all', status: 'all' });
    } else {
      void fetchActivity(true);
    }
    if (tenant?.id) {
      void subscribeActivityRealtime(tenant.id);
    }
    return () => {
      unsubscribeActivityRealtime();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);

  const [typeFilter, setTypeFilter] = useState<ActivityTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<ActivityStatusFilter>('all');
  const [search, setSearch] = useState('');

  const handleType = (value: string) => {
    const v = value as ActivityTypeFilter;
    setTypeFilter(v);
    // Servidor refaz a página com o kind mapeado; pipeline/tool → handler
    // (refinados client-side em filterActivityFeed — sem kind dedicado na API).
    setActivityFilters({ kind: typeToKindParam(v) });
  };

  const handleStatus = (value: string) => {
    const v = value as ActivityStatusFilter;
    setStatusFilter(v);
    setActivityFilters({ status: statusToBackendParam(v) });
  };

  // Predicado completo sobre a lista em memória: realtime/polling inserem rows
  // sem filtro de kind/status, e busca é 100% client-side.
  const visible = useMemo(
    () => filterActivityFeed(activity, { type: typeFilter, status: statusFilter, search }),
    [activity, typeFilter, statusFilter, search],
  );

  const hasActiveFilter = typeFilter !== 'all' || statusFilter !== 'all' || search.trim() !== '';

  return (
    <>
      <div className="p-4 md:p-6 pt-12">
        <PageHeader title="Atividade" />

        {/* Filtros — wireframe .dfilters: busca + Tipo + Status */}
        <div className="wf-dfilters">
          <div className="wf-search" style={{ flex: 1, minWidth: 170 }}>
            <Search className="ic" aria-hidden="true" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar atividade"
              aria-label="Buscar atividade"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => handleType(e.target.value)}
            aria-label="Filtrar por tipo"
          >
            {TYPE_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => handleStatus(e.target.value)}
            aria-label="Filtrar por status"
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Feed — wireframe .dblock + .dblock-h + .afeed.big */}
        <div className="wf-dblock" style={{ marginTop: 14 }}>
          <div className="wf-dblock-h">
            <Activity className="ic" aria-hidden="true" />
            Atividade
            <RealtimeIndicator />
          </div>

          {activityLoading && activity.length === 0 ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <WfEmpty icon={<Activity />}>
              <div className="font-semibold text-raios-text">
                {hasActiveFilter ? 'Nada encontrado' : 'Nenhuma execução ainda'}
              </div>
              {hasActiveFilter
                ? 'Nenhuma atividade corresponde aos filtros atuais.'
                : 'As execuções de pipelines, skills, agentes, companions e tools aparecerão aqui em tempo real.'}
            </WfEmpty>
          ) : (
            <div role="list" aria-label="Feed de atividade" className="wf-feed wf-feed--fill">
              {visible.map((entry) => (
                <ActivityFeedRow
                  key={`${entry.kind}-${entry.id}`}
                  item={entry}
                  onClick={(it) => void openActivityDetail(it)}
                />
              ))}
            </div>
          )}

          {/* Carregar mais (cursor pagination do RPC) — anatomia .wf-btn ghost */}
          {activityHasMore && (
            <div className="wf-act-loadmore">
              <button
                type="button"
                className="wf-btn wf-btn-ghost"
                onClick={() => void loadMoreActivity()}
                disabled={activityLoading}
              >
                {activityLoading ? (
                  <>
                    <Loader2 className="ic animate-spin" aria-hidden="true" />
                    Carregando…
                  </>
                ) : (
                  'Carregar mais'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <ActivityDetailModal />
    </>
  );
}
